import React from "react";
import {
  PublicBotData,
  QueryStatus,
  BotData,
  BotGeneralData,
  BotSymbolPriceData,
  BotType,
  CreateBotRequest,
  EditBotsRequest,
  StrippedBotData,
  AmountType,
  BotsBulkGeneralData,
  BaseBotGeneralData,
  OverrideFieldType,
  BaseBotData,
  BotConfigurationType,
  SymbolsMetadataManager,
  GetBotsDataResponse,
  ExchangeToSymbolPrices,
  GroupData,
} from "@shared/types";
import { BotAmountData } from "@shared/features/bots/types";
import {
  BaseBotFormValues,
  BotBulkActionData,
  BotFormSetters,
  BotFormType,
  BotFormValues,
  Error,
  FormDataType,
  InitialBotAmountData,
  InitializedData,
  OverlapData,
  RestoreTabChangesData,
  SignalsMainCoinsData,
  Steps,
  StrategyOrder,
  ToggleField,
} from "@shared/features/bots/BotForm/common/types";

export const PreserveBotFormValuesContext = React.createContext(false);

export const BotFormContext = React.createContext<BotFormData | undefined>(
  undefined
);

export type BotFormData = {
  botGeneralData?: BotGeneralData | BotData | BaseBotGeneralData;
  isEdit: boolean;
  isPublic: boolean;
  isDuplicate: boolean;
  botId?: number;
  botType: BotType;
  addError: (error: Error) => void;
  removeError: (error: Error) => void;
  errors: Error[];
  toggleField: (field: ToggleField, checked: boolean) => void;
  toggleOverrideField: (field: OverrideFieldType, checked: boolean) => void;
  botAmountData?: BotAmountData;
  getBotAmountDataStatus: QueryStatus;
  getMinAmountByPrice: (price: number) => number;
  parsedEntries?: StrategyOrder[];
  parsedValidationEntries?: StrategyOrder[];
  parsedEntriesAndGridTakeProfits?: StrategyOrder[];
  parsedTakeProfits?: StrategyOrder[];
  parsedStop?: StrategyOrder;
  symbolPriceData?: BotSymbolPriceData;
  isSymbolValid?: boolean;
  isMarginExchange: boolean;
  isFetching: boolean;
  isLoading: boolean;
  isError: boolean;
  editBotGeneralData?: BotData;
  publicBotGeneralData?: PublicBotData;
  getParsedFormData: (ignoreErrors?: Error[]) => CreateBotRequest | undefined;
  getParsedBulkEditFormData?: () => EditBotsRequest | undefined;
  botFormType: BotFormType;
  validateOrderConvertedAmount: (amount: number, price: number) => boolean;
  getBotCreateGeneralDataStatus: QueryStatus<BotGeneralData>;
  getBulkBotCreateGeneralDataStatus: QueryStatus<BotsBulkGeneralData>;
  getExchangeToSymbolPricesStatus: QueryStatus<ExchangeToSymbolPrices>;
  getExchangeSymbolPriceStatus: QueryStatus<BotSymbolPriceData>;
  getGroupsDataStatus: QueryStatus<GroupData[]>;
  addInitializedData: (formDataType: FormDataType) => void;
  idToValue?: Record<number, number | null>;
  refetchErrorQueries: () => void;
  formValues: BotFormValues;
  formSetters: BotFormSetters;
  tabToRestoreChangesData?: Record<Steps, RestoreTabChangesData | undefined>;
  botsData?: BotData[];
  originalBotValues?: BaseBotFormValues;
  bulkActionBotsData?: BotBulkActionData[];
  bulkActionValidBotsData?: BotBulkActionData[];
  bulkActionInvalidBotsData?: BotBulkActionData[];
  fieldsToInitialize?: Set<keyof BaseBotFormValues>;
  addFieldToInitialize?: (field: keyof BaseBotFormValues) => void;
  removeFieldToInitialize?: (field: keyof BaseBotFormValues) => void;
  isAdvancedView: boolean;
  setIsAdvancedView: (isAdvancedView: boolean) => void;
  isChannelConfiguration?: boolean;
  getAmountTypePrecision: (amountType?: AmountType) => number;
  amountPrecision: number;
  initialBotAmountData: InitialBotAmountData;
  tab: Steps;
  setTab: (tab: Steps) => void;
  initializeFormValues: (
    bot: BotData | BaseBotData | StrippedBotData | undefined,
    botsNames?: Record<number, string>
  ) => void;
  accountsOverlapData?: OverlapData;
  getBotsDataStatus: QueryStatus<GetBotsDataResponse>;
  isBacktesting?: boolean;
  backtestingValuesModified?: boolean;
  setBacktestingValuesModified?: (backtestingValuesModified: boolean) => void;
  backtestBotData?: BotData;
  restoreDefaultValues?: () => void;
  backtestingBotId?: number;
  amountChangedRef: React.RefObject<boolean>;
  isCopyToWorkspace?: boolean;
  initializedData: InitializedData;
  showBacktesting?: boolean;
  configurationType?: BotConfigurationType;
  subGroupId?: number;
  signalsMainCoinsData?: SignalsMainCoinsData;
  symbolsMetadata?: SymbolsMetadataManager;
  botUUID?: string;
  showPublicBots?: boolean;
  isPublicBotApplied?: boolean;
  shouldInitialSymbolAndAccount: boolean;
  setShouldInitialSymbolAndAccount: (value: boolean) => void;
} & BotFormValues &
  BotFormSetters;
